import React from "react";
import { Field, change } from "redux-form";
import Step from "../Step";

import styles from "../styles.module.css";
import WithUserForm from "../../Register/WithUserForm";
import withHooks from "react-with-hooks";
import CTAButton from "../../utils/Button/CTAButton";
import { dataFieldsOptions } from "@schmodel/core";

const brandTypesFashionAndAccessories = dataFieldsOptions.brandTypes.filter(
  b => b.group === 'Fashion apparel' || b.group === 'Fashion accessories'
);
const brandTypesBeauty = dataFieldsOptions.brandTypes.filter(
  b => b.group === 'Beauty products'
);

const onCameraSkills = dataFieldsOptions.skills.filter(s => s.onCamera);
const offCameraSkills = dataFieldsOptions.skills.filter(s => !s.onCamera);

export const BRAND_NAME_MAX = 35;
export const BRAND_DESCRIPTION_MIN = 33;
export const BRAND_DESCRIPTION_MAX = 88;

export const isBrandValid = (brandName, brandDescription) => {
  const name = (brandName || '').trim();
  const desc = (brandDescription || '').trim();
  return name.length > 0 &&
    desc.length >= BRAND_DESCRIPTION_MIN &&
    desc.length <= BRAND_DESCRIPTION_MAX;
};

const BrandFields = ({ brandName, brandDescription, setBrandName, setBrandDescription }) => {
  // Show the red border only after the field has been touched (blurred), so pristine
  // fields don't start red.
  const [nameTouched, setNameTouched] = React.useState(false);
  const [descTouched, setDescTouched] = React.useState(false);
  const nameInvalid = nameTouched && brandName.trim().length === 0;
  const descInvalid = descTouched && brandDescription.trim().length < BRAND_DESCRIPTION_MIN;
  return (
    <div className={styles.brand_fields}>
      <input
        type="text"
        className={`${styles.brand_name_input} ${nameInvalid ? styles.brand_input_error : ''}`}
        placeholder="Brand name"
        maxLength={BRAND_NAME_MAX}
        value={brandName}
        // Alphanumeric (letters, digits and spaces) only
        onChange={(e) => setBrandName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ''))}
        onBlur={() => setNameTouched(true)}
      />
      <div className={styles.brand_description_wrap}>
        <textarea
          className={`${styles.brand_description_input} ${descInvalid ? styles.brand_input_error : ''}`}
          maxLength={BRAND_DESCRIPTION_MAX}
          value={brandDescription}
          onChange={(e) => setBrandDescription(e.target.value)}
          onBlur={() => setDescTouched(true)}
        />
        {!brandDescription && (
          <div className={styles.brand_description_placeholder}>
            <span className={styles.brand_description_placeholder_title}>Brand description </span>
            <span className={styles.brand_description_placeholder_hint}>
              (describe your brand in {BRAND_DESCRIPTION_MIN} - {BRAND_DESCRIPTION_MAX} characters)
            </span>
          </div>
        )}
      </div>
    </div>
  );
};


const Tag = ({ item, isSelected, onPress, style = {}}) => (
  <button
    type="button"
    className={`${styles.tag} ${style} ${isSelected ? styles.skill_button_selected : ''}`}
    onClick={() => onPress(item.value)}
  >
    {item.label}
  </button>
);

const SelectedSkills = withHooks((props) => {
  const {
    activeTab, checkedItems, handleToggleItem,
    brandName, brandDescription, setBrandName, setBrandDescription,
  } = props;

  if (activeTab === 'talent') {
    return (
      <div className={styles.skills_categories}>
        <p className={styles.section_label}>On-Camera</p>
        <div className={styles.skill_buttons_grid}>
          {onCameraSkills.map(item => (
            <Tag
              key={item.value}
              item={item}
              isSelected={checkedItems.includes(item.value)}
              onPress={handleToggleItem}
            />
          ))}
        </div>
        <p
            className={styles.section_label}
           style={{ marginTop: '3vh' }}
        >Off-Camera</p>
        <div className={styles.skill_buttons_grid}>
          {offCameraSkills.map(item => (
            <Tag
              key={item.value}
              item={item}
              isSelected={checkedItems.includes(item.value)}
              onPress={handleToggleItem}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.skills_categories}>
      <BrandFields
        brandName={brandName}
        brandDescription={brandDescription}
        setBrandName={setBrandName}
        setBrandDescription={setBrandDescription}
      />
      <p className={styles.section_label}>Fashion products</p>
      <div className={styles.skill_buttons_grid}>
        {brandTypesFashionAndAccessories.map(item => (
          <Tag
            key={item.value}
            item={item}
            isSelected={checkedItems.includes(item.value)}
            onPress={handleToggleItem}
            style={styles.fashion_types}
          />
        ))}
      </div>
      <p className={styles.section_label} style={{ marginTop: '3vh' }}>Beauty products</p>
      <div className={styles.skill_buttons_grid}>
        {brandTypesBeauty.map(item => (
          <Tag
            key={item.value}
            item={item}
            isSelected={checkedItems.includes(item.value)}
            onPress={handleToggleItem}
            style={styles.beauty_types}
          />
        ))}
      </div>
    </div>
  );
});

export default class StepSkills extends React.Component {
  constructor(props) {
    super(props);
    this.initialized = false;
    this.state = {
      activeTab: 'talent',
      checkedItems: [],
      brandName: '',
      brandDescription: '',
      isError: false,
    };
  }

  setBrandName = (brandName) => this.setState({ brandName });
  setBrandDescription = (brandDescription) => this.setState({ brandDescription });

  initFromUser = (currentUser) => {
    // Fill checkedItems from the userdata once. this.initialized prevents this from
    // running again on subsequent renders (every skill toggle calls setState -> re-render),
    // which would overwrite the user's in-progress selections.
    if (this.initialized || !currentUser) return;
    this.initialized = true;
    const hasBrandTypes = currentUser.brandTypes?.length > 0;
    const hasSkills = currentUser.skills?.length > 0;
    this.setState({
      activeTab: hasBrandTypes ? 'brand' : 'talent',
      checkedItems: hasBrandTypes ? currentUser.brandTypes : (hasSkills ? currentUser.skills : []),
      brandName: currentUser.brandName || '',
      brandDescription: currentUser.brandDescription || '',
    });
  };

  setActiveTab = (tab) => {
    this.setState({ activeTab: tab, checkedItems: [] });
  };

  handleToggleItem = (value) => {
    const { checkedItems } = this.state;
    if (checkedItems.includes(value)) {
      this.setState({ checkedItems: checkedItems.filter(v => v !== value) });
    } else {
      this.setState({ checkedItems: [...checkedItems, value] });
    }
  };

  async onSubmit(values, _, props) {
    const { activeTab, checkedItems, brandName, brandDescription } = this.state;
    if (checkedItems.length === 0) {
      this.setState({ isError: true });
      return;
    }
    if (activeTab === 'talent') {
      props.dispatch(change('signup', 'skills', checkedItems));
      const formattedValues = WithUserForm.formatUserSkillsInput({ ...values, skills: checkedItems });
      await props.updateUserSkills({ variables: { input: formattedValues } });
    } else {
      if (!isBrandValid(brandName, brandDescription)) {
        this.setState({ isError: true });
        return;
      }
      props.dispatch(change('signup', 'brandTypes', checkedItems));
      const formattedValues = WithUserForm.formatUserBrandInput({
        ...values,
        brandTypes: checkedItems,
        brandName: brandName.trim(),
        brandDescription: brandDescription.trim(),
      });
      await props.updateUserBrand({ variables: { input: formattedValues } });
    }
    return props.machine.service.send({ type: "NEXT" });
  }

  render() {
    const { activeTab, checkedItems, brandName, brandDescription } = this.state;
    const nextDisabled = checkedItems.length === 0 ||
      (activeTab === 'brand' && !isBrandValid(brandName, brandDescription));
    return (
      <WithUserForm
        onSubmit={(values, _, props) => this.onSubmit(values, _, props)}
      >
        {({form: { handleSubmit }, userQuery: { data }}) => {
          this.initFromUser(data?.currentUser);
          const footer = (
              <div
                  style={{
                    width: '90vw',
                    maxWidth: '400px',
                    minHeight: activeTab === 'talent' ? '70px' : '150px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingBottom: '10px'
                  }}
              >
                <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                  {/* Center cta button in available space between text and footer*/}
                  <CTAButton
                      onClick={handleSubmit}
                      disabled={nextDisabled}
                      text={'Next'}
                  />
                </div>
                <p
                    className={` ${styles.default_text_size}`}
                    style={{
                      color: '#B08D57',
                      lineHeight: 0.9
                    }}
                >
                  Having trouble? <a href="https://wa.me/443330166335" style={{ textDecoration: 'underline', fontWeight: 'bold' }}>Contact us</a>.
                </p>
              </div>
          );
          return (
            <Step footer={footer} scrollable>
              <p
                className="text-white text-md"
                style={{
                  fontWeight: 700,
                  fontSize: 'clamp(1px, 4vw, 18px)' ,
                  marginTop: activeTab === 'talent' ? 0 : '50px'
              }}
              >What do you do?</p>
              <div className={styles.tab_bar}>
                <button
                  type="button"
                  className={`${styles.tab} ${activeTab === 'talent' ? styles.tab_active : ''}`}
                  onClick={() => this.setActiveTab('talent')}
                >
                  I am Creative Talent
                </button>
                <button
                  type="button"
                  className={`${styles.tab} ${activeTab === 'brand' ? styles.tab_active : ''}`}
                  onClick={() => this.setActiveTab('brand')}
                >
                  I am a Brand
                </button>
              </div>
              <form onSubmit={handleSubmit} className={styles.form_step_skills}>
                <Field
                  name="skills"
                  component={SelectedSkills}
                  activeTab={activeTab}
                  checkedItems={checkedItems}
                  handleToggleItem={this.handleToggleItem}
                  brandName={brandName}
                  brandDescription={brandDescription}
                  setBrandName={this.setBrandName}
                  setBrandDescription={this.setBrandDescription}
                />
              </form>
            </Step>
          );
        }}
      </WithUserForm>
    );
  }
}
